import { useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";

const Side = ({ s }) => (
  <div className="min-w-0 flex-1">
    <div className="truncate text-sm" style={{ color: s.color }}>{s.title}</div>
    <div className="truncate text-xs text-[#A8A090]">{s.calendar} · {s.label}</div>
  </div>
);

export const IntersectionsPanel = ({ uid, calendars, base }) => {
  const y = base.today.year;
  const [range, setRange] = useState({ from: y, to: y + 10 });
  const [ids, setIds] = useState(calendars.map((c) => c.id));
  const [moons, setMoons] = useState(false);
  const [cross, setCross] = useState(false);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/universes/${uid}/calendars/intersections`, {
        base_calendar_id: base.id, from_year: parseInt(range.from, 10), to_year: parseInt(range.to, 10), calendar_ids: ids, include_moons: moons, cross_only: cross,
      });
      setRes(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const toggle = (id) => setIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  return (
    <div className="space-y-6" data-testid="intersections-panel">
      <div className="flex flex-wrap items-end gap-4 rounded-md border border-[#2D3442] bg-[#13171F]/60 p-4">
        <div className="space-y-1.5"><Label>From year ({base.name})</Label><Input type="number" className="w-32 font-mono-lf" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="intersect-from-input" /></div>
        <div className="space-y-1.5"><Label>To year</Label><Input type="number" className="w-32 font-mono-lf" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="intersect-to-input" /></div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={moons} onCheckedChange={setMoons} data-testid="intersect-moons-switch" />Include moon phases</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={cross} onCheckedChange={setCross} data-testid="intersect-cross-switch" />Across calendars only</label>
        <Button onClick={run} disabled={busy || !ids.length} data-testid="intersect-run-button"><Search className="mr-1.5 h-4 w-4" />{busy ? "Searching…" : "Find intersections"}</Button>
        <div className="flex w-full flex-wrap gap-3">
          {calendars.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm text-[#A8A090]">
              <Checkbox checked={ids.includes(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`intersect-cal-${c.id}`} />
              <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />{c.name}
            </label>
          ))}
        </div>
      </div>
      {res && (
        <section>
          <div className="lf-label mb-3" data-testid="intersect-count">{res.count} intersections{res.truncated ? " (showing first 500)" : ""}</div>
          <div className="divide-y divide-[#2D3442] rounded-md border border-[#2D3442]" data-testid="intersect-results">
            {res.results.map((r, i) => (
              <div key={i} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4" data-testid={`intersect-row-${i}`}>
                <span className="w-56 shrink-0 font-mono-lf text-xs text-[#F59E0B]">{r.base_label}</span>
                <Side s={r.a} /><span className="hidden text-[#A8A090] sm:inline">×</span><Side s={r.b} />
              </div>
            ))}
            {res.count === 0 && <p className="p-4 text-sm text-[#A8A090]">No events coincide in this range.</p>}
          </div>
        </section>
      )}
    </div>
  );
};
